import React, { useState, useEffect, useRef } from 'react';
import { contentItemsAPI } from '../../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Search, BookOpen, ArrowRight } from 'lucide-react';

export const CatalogLinkDialog = ({ open, onOpenChange, onSelect, showSkip = true, title = 'Link to Partner Skilling Catalog', description = 'Search for the course in catalog.skillupwithlevelup.com to pull its metadata.' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef();

  useEffect(() => {
    if (!open) { setQuery(''); setResults([]); setSearched(false); return; }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await contentItemsAPI.searchCatalog(query);
        setResults(data);
      } catch {
        setResults([]);
      } finally { setSearching(false); setSearched(true); }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="catalog-link-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Search catalog course title..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="pl-10"
              data-testid="catalog-search-input"
            />
          </div>
          <div className="max-h-80 overflow-y-auto space-y-2" data-testid="catalog-search-results">
            {searching ? (
              <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />)}</div>
            ) : results.length > 0 ? (
              results.map(course => (
                <button
                  key={course.course_id}
                  onClick={() => onSelect(course)}
                  className="w-full flex items-center justify-between gap-3 text-left border border-border rounded-lg p-3 hover:border-lime hover:bg-lime/5 transition-colors"
                  data-testid={`catalog-course-result-${course.course_id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{course.title}</p>
                    <div className="flex items-center gap-1.5 flex-wrap mt-1">
                      {(course.solution_area || []).map(sa => <Badge key={sa} variant="outline" className="text-[10px]">{sa}</Badge>)}
                      {course.course_type && <Badge variant="outline" className="text-[10px] bg-slate-50">{course.course_type}</Badge>}
                      {course.level && <span className="text-[11px] text-muted-foreground">{course.level}</span>}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </button>
              ))
            ) : searched ? (
              <p className="text-sm text-muted-foreground text-center py-6">No matching courses found in the catalog.</p>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-6">Start typing to search the catalog...</p>
            )}
          </div>
          {showSkip && (
            <Button variant="outline" className="w-full" onClick={() => onSelect(null)} data-testid="catalog-skip-btn">
              <BookOpen className="w-4 h-4 mr-2" />This course isn't in the Catalog yet
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CatalogLinkDialog;
